import _ from "lodash";
import { useMemo } from "react";
import { useLeadStore, useLeaseCompStore, useLocationStore, usePipedriveStore, useSaleCompStore, useTenantInMarketStore } from "..";
import IPipedriveOrganization from "../interfaces/IPipedriveOrganization";
import useTenantsInMarketWithGlobalFilters from "../interfaces/useTenantsInMarketWithGlobalFilters";
import useDealsWithFocusArea from "./useDealsWithFocusArea";
import useDealsWithGlobalFilters from "./useDealsWithGlobalFilters";
import useLeadsWithFocusArea from "./useLeadsWithFocusArea";
import useLeadsWithGlobalFilters from "./useLeadsWithGlobalFilters";
import useLeaseCompsWithFocusArea from "./useLeaseCompsWithFocusArea";
import useLeaseCompsWithGlobalFilters from "./useLeaseCompsWithGlobalFilters";
import useSaleCompsWithFocusArea from "./useSaleCompsWithFocusArea";
import useSaleCompsWithGlobalFilters from "./useSaleCompsWithGlobalFilters";
import useTenantsInMarketWithFocusArea from "./useTenantsInMarketWithFocusArea";

export default function useCompanies(searchFilter: string, sortBy: string) {
    const pipedriveStore = usePipedriveStore();
    const locationStore = useLocationStore();
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const deals = useDealsWithFocusArea(useDealsWithGlobalFilters(pipedriveStore.deals));
    const leads = useLeadsWithFocusArea(useLeadsWithGlobalFilters(leadStore.leads));
    const leaseComps = useLeaseCompsWithFocusArea(useLeaseCompsWithGlobalFilters(leaseCompStore.leaseComps));
    const saleComps = useSaleCompsWithFocusArea(useSaleCompsWithGlobalFilters(saleCompStore.saleComps));
    const tenantsInMarket = useTenantsInMarketWithFocusArea(useTenantsInMarketWithGlobalFilters(tenantInMarketStore.tenantsInMarket));

    const sortedOrganizations: IPipedriveOrganization[] = useMemo(() => {
        const direction = sortBy.endsWith("Asc") ? "asc" : "desc";
        const orderByCount = (counts: _.Dictionary<number>) => _.orderBy(pipedriveStore.organizations, o => counts[o.id] ?? 0, direction);

        switch (sortBy) {
            case CompaniesSortingType.LocationsAsc:
            case CompaniesSortingType.LocationsDesc:
                return orderByCount(_.countBy(locationStore.locations, l => l.pipedriveCompanyId));
            case CompaniesSortingType.DealsAsc:
            case CompaniesSortingType.DealsDesc:
                return orderByCount(_.countBy(deals, d => d.contactCompanyId));
            case CompaniesSortingType.LeadsAsc:
            case CompaniesSortingType.LeadsDesc:
                return orderByCount(_.countBy(leads, l => l.pipedriveCompanyId));
            case CompaniesSortingType.LeaseCompsAsc:
            case CompaniesSortingType.LeaseCompsDesc:
                return orderByCount(_.countBy(leaseComps, lc => lc.pipedriveCompanyId));
            case CompaniesSortingType.SaleCompsAsc:
            case CompaniesSortingType.SaleCompsDesc:
                return orderByCount(_.countBy(saleComps.flatMap(sc => _.uniq([sc.pipedriveBuyerId, sc.pipedriveSellerId, sc.pipedriveBrokerageId].filter(id => id != null))), id => id));
            case CompaniesSortingType.TIMsAsc:
            case CompaniesSortingType.TIMsDesc:
                return orderByCount(_.countBy(tenantsInMarket, tim => tim.pipedriveCompanyId));
            case CompaniesSortingType.CreatedNewest:
                return _.orderBy(pipedriveStore.organizations, o => o.createdDate, "desc");
            default:
                return _.orderBy(pipedriveStore.organizations, o => o.createdDate, "asc");
        }
    }, [pipedriveStore.organizations, sortBy, locationStore.locations, deals, leads, leaseComps, saleComps, tenantsInMarket]);

    return useMemo(() => {
        if (!searchFilter)
            return sortedOrganizations;

        const searchFilterInLowerCase = searchFilter.toLowerCase();

        return sortedOrganizations.filter(o =>
            o.name && o.name.toLowerCase().includes(searchFilterInLowerCase)
        );
    }, [sortedOrganizations, searchFilter]);
}

export class CompaniesSortingType {
    static LocationsAsc = "LocationsAsc";
    static LocationsDesc = "LocationsDesc";
    static DealsAsc = "DealsAsc";
    static DealsDesc = "DealsDesc";
    static LeadsAsc = "LeadsAsc";
    static LeadsDesc = "LeadsDesc";
    static LeaseCompsAsc = "LeaseCompsAsc";
    static LeaseCompsDesc = "LeaseCompsDesc";
    static SaleCompsAsc = "SaleCompsAsc";
    static SaleCompsDesc = "SaleCompsDesc";
    static TIMsAsc = "TIMsAsc";
    static TIMsDesc = "TIMsDesc";
    static CreatedNewest = "CreatedNewest";
    static CreatedOldest = "CreatedOldest";
}